/**
 * ActionCommandSelect Component
 *
 * Mobile-friendly action selector using ResponsiveCommandSelect.
 */

import React from "react";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";
import { ResponsiveCommandSelect, ResponsiveSelect } from "@/components/ui/responsive-select";
import type { SelectOption } from "@/components/ui/responsive-select/types";
import type { DetailAction, BaseEntity } from "../types/detailPage";

interface ActionCommandSelectProps<T extends BaseEntity> {
  actions: DetailAction<T>[];
  item: T;
  onActionSelect: (actionId: string) => void;
  placeholder?: string;
  expandInline?: boolean;
  expandInlineVariant?: "replace" | "floating";
  className?: string;
}

const ActionCommandSelectBase: React.FC<ActionCommandSelectProps<any>> = ({
  actions,
  item,
  onActionSelect,
  placeholder = "Select Action",
  expandInline = false,
  expandInlineVariant = "replace",
  className = "",
}) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const actionOptions: SelectOption[] = actions.map((action) => {
    const label =
      typeof action.label === "function" ? action.label(item) : action.label;
    return {
      value: action.id,
      label: label,
      description: undefined,
      disabled:
        action.disabled &&
        typeof action.disabled === "function" &&
        action.disabled(item),
      searchableText: label,
    };
  });

  if (isDesktop) {
    return (
      <ResponsiveCommandSelect
        value=""
        onValueChange={onActionSelect}
        options={actionOptions}
        placeholder={placeholder}
        title="Actions"
        description="Choose an action to perform"
        searchPlaceholder="Search actions..."
        className={className}
        expandInline={expandInline}
        expandInlineVariant={expandInlineVariant}
      />
    );
  }

  return (
    <ResponsiveSelect
      value=""
      onValueChange={onActionSelect}
      options={actionOptions}
      placeholder={placeholder}
      title="Actions"
      description="Choose an action to perform"
      className={className}
    />
  );
};

export const ActionCommandSelect = React.memo(ActionCommandSelectBase);
